import { LiraContainerIds } from '@client/components/Adv/Lira/liraContainerIds';
import { LiraLazySlotBelowListing } from '@client/components/Adv/Lira/LiraLazySlotBelowListing';
import { FloatingButtonTrackingEvents } from '@client/components/Scaffolding/FloatingButton';
import { SnackBarTrackingEvents } from '@client/components/Scaffolding/SnackBar';
import ListingBreadcrumbs from '@client/components/SEO/Breadcrumbs/ListingBreadcrumbs';
import { ListingLinks } from '@client/components/SEO/InternalLinks/types';
import type { RootState } from '@reducers/index';
import type { ItemsState } from '@reducers/items';
import {
  declareFinalSearch,
  type RadiusSearch,
  resetFilters,
  type SearchState,
  setFiltersDialogStatus,
} from '@reducers/search';
import { setPreventPopstate } from '@reducers/system';
import { LiraAdvContainer } from '@sbt-web/adv';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { AdTypes, CategoryID } from '@sbt-web/network/types';
import type { Faq as FaqType } from '@sbt-web/network/types';
import { GeoEntry, SeoMetaPayload, TuttaItalia } from '@sbt-web/networking';
import type { RecentSearchOption } from '@sbt-web/search-autocomplete';
import { Button, DeviceWidth, FullModal } from '@sbt-web/ui';
import { readFromLocalStorage } from '@sbt-web/utils';
import { Category, getMacroOfCategoryID } from '@sbt-web/category-utils';
import {
  onEngage,
  onSave,
  onUnsave,
} from '@shared/helpers/Favorites/trackingEvents';
import { useSavedSearches } from '@shared/helpers/SavedSearches';
import { areSameGeo, areSameRadius } from '@tools/geoValuesHelper';
import { MsiteGAMIds } from '@tools/listingPlugins/commons';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect, useState } from 'react';
import { batch, connect } from 'react-redux';
import InternalLinksFooter from '../../SEO/InternalLinks/Footer';
import InternalLinksHeader from '../../SEO/InternalLinks/Header';
import InternalLinksSidebar from '../../SEO/InternalLinks/Sidebar';
import MostSearched from '@client/components/SEO/InternalLinks/MostSearched';
import Sitemap from '@client/components/SEO/Sitemap';
import PaginationSelector from '../Elements/Pagination';
import SearchBar from '../Elements/SearchBar';
import TopBar from '../Elements/TopBar';
import Faq from '../Elements/Faq';
import ListingShallowRouter from '../ListingShallowRouter';
import FavoritesContainer from './FavoritesContainer';
import ItemListContainer from './ItemListContainer';
import { type FiltersConfigState } from '@reducers/filtersConfig';
import ownClasses from './ListingContainer.module.scss';
import gridStyles from '@client/components/Detail/Containers/style/grid.module.scss';

const FiltersContainer = dynamic(() => import('./FiltersContainer'), {
  ssr: false,
});

const FloatingButton = dynamic(
  () => import('@client/components/Scaffolding/FloatingButton'),
  { ssr: false }
);

const SnackBar = dynamic(
  () => import('@client/components/Scaffolding/SnackBar'),
  { ssr: false }
);

const TextLinkBox = dynamic(() => import('./TextLinkBox/TextLinkBox'), {
  ssr: false,
});

const PremiumSavedSearchExperiment = dynamic(
  () => import('@client/components/Adv/PremiumSavedSearch'),
  {
    ssr: false,
  }
);

interface OwnProps {
  ads?: string[];
  seo?: SeoMetaPayload;
  handleItemClick: () => void;
  internalLinks: ListingLinks | null;
}

interface StateProps {
  filtersDialogOpen?: boolean;
  query?: string;
  category: Category;
  isRadiusSearch?: boolean;
  geo: GeoEntry;
  searchNearRegions: boolean;
  filters: Record<string, string>;
  qso: boolean;
  shp: boolean;
  urg: boolean;
  adType: AdTypes;
  radiusSearch: RadiusSearch | null;
  search: SearchState;
  items: ItemsState;
  filtersConfig: FiltersConfigState;
}

interface DispatchProps {
  openFiltersDialog: () => void;
  resetFilters: () => void;
  acceptSearch: () => void;
}

interface LCFaqProps {
  faqData: FaqType | null;
}

export type Props = OwnProps & StateProps & DispatchProps & LCFaqProps;

const localStorageKey = 'recent_autocomplete_searches';

const storeRecentSearch = (recentSearch: RecentSearchOption) => {
  try {
    const recentSearches = readFromLocalStorage<RecentSearchOption[]>(
      localStorageKey,
      []
    );

    if (
      recentSearch.query === '' &&
      recentSearch.category.id === CategoryID.Tutte &&
      recentSearch.geo.geoValues.region.id === TuttaItalia.id
    ) {
      return;
    } else if (recentSearches?.length === 0) {
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify([recentSearch])
      );
    } else {
      const olderSearches = recentSearches?.filter((olderSearch) => {
        const queryUnequal = olderSearch.query !== recentSearch.query;
        const categoryUnequal =
          olderSearch.category.id !== recentSearch.category.id;
        const nearRegionsUnequal =
          olderSearch.searchNearRegions !== recentSearch.searchNearRegions;
        const geoUnequal = !areSameGeo(
          olderSearch.geo.geoValues,
          recentSearch.geo.geoValues,
          olderSearch.searchNearRegions,
          recentSearch.searchNearRegions
        );
        const radiusUnequal = !areSameRadius(
          olderSearch.geo.radiusValues,
          recentSearch.geo.radiusValues
        );

        return (
          queryUnequal ||
          categoryUnequal ||
          nearRegionsUnequal ||
          geoUnequal ||
          radiusUnequal
        );
      });
      // Put the search at the top as the most recent one and take 2 elements
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify([recentSearch, ...(olderSearches ?? [])].slice(0, 3))
      );
    }
  } catch {
    console.error('Could not set recent searches');
  }
};

export const ListingContainer: React.FunctionComponent<Props> = (props) => {
  const { isMobile, isDesktop } = useViewport();

  const { openFiltersDialog, resetFilters, acceptSearch, handleItemClick } =
    props;

  const {
    filtersDialogOpen = false,
    seo,
    internalLinks,
    faqData,
    query,
    category,
    geo,
    searchNearRegions,
    filters,
    qso,
    shp,
    adType,
    radiusSearch,
    search,
    urg,
    items,
  } = props;

  useEffect(() => {
    if (isMobile && radiusSearch != null) {
      // Do not save mobile radius searches: with the preview we save too often
      return;
    } else if (query === undefined || !/\S/.test(query)) {
      return;
    } else {
      // Store a new search in localstorage
      storeRecentSearch({
        adType,
        query,
        category,
        geo: {
          geoValues: geo,
          radiusValues: radiusSearch,
        },
        searchNearRegions,
        allFilters: {
          qso,
          shp,
          urg,
          filters,
        },
      });
    }
  }, [
    adType,
    query,
    category,
    geo,
    filters,
    qso,
    shp,
    urg,
    radiusSearch,
    searchNearRegions,
    isMobile,
  ]);

  const { optimizely } = useContext(OptimizelySubitoContext);
  const [enableOverrideUrgentForPro, setEnableOverrideUrgentForPro] =
    useState(false);

  const savedSearchProvider = useSavedSearches(
    isMobile ? new FloatingButtonTrackingEvents() : new SnackBarTrackingEvents()
  );

  useEffect(() => {
    if (optimizely) {
      optimizely
        .onReady()
        .then(() =>
          setEnableOverrideUrgentForPro(
            optimizely.isFeatureEnabled(
              'subito.web.visibility-options.occasione.tag'
            )
          )
        );
    }
  }, [optimizely]);

  return (
    <div>
      <style>
        {`
        @media (max-width: ${DeviceWidth.mobile.max}) {
          /* This should be the banner for apps flyer for the installation
          of the APP */
          #af-smart-banner.afb-container {
            z-index: 101;
          }
        }
      `}
      </style>
      <NoScriptSeoCss />
      <div data-google-interstitial="false">
        <ListingShallowRouter />
        {internalLinks !== null ? (
          <InternalLinksHeader links={internalLinks.header} />
        ) : null}
        <ListingBreadcrumbs search={search} />
        <SearchBar onFiltersOpen={openFiltersDialog} />
      </div>

      <div className={ownClasses.layout}>
        {isDesktop && <Skyscrapers />}
        <div className={ownClasses['layout__main']}>
          <div data-google-interstitial="false">
            {isDesktop && (
              <LiraAdvContainer
                className={classnames(gridStyles['detail-component'], ownClasses.listingTop)}
                style={{
                  height: `250px`,
                  display: 'block',
                  alignContent: 'center',
                }}
                withPlaceholder
                idForAd={LiraContainerIds.listing.top}
              />
            )}
            <TopBar />
          </div>

          <div className={classnames(ownClasses.container)}>
            {isMobile ? null : (
              <div
                className={classnames(
                  ownClasses.col,
                  ownClasses.filters,
                  'col',
                  'filters'
                )}
              >
                <div data-google-interstitial="false">
                  <FiltersContainer />
                  <TextLinkBox page="listing" max_number_textlinks={1} />
                </div>
                <div id="banner_container" className={ownClasses.adv}>
                  <LiraAdvContainer
                    idForAd={LiraContainerIds.listing.aside}
                    style={{
                      display: 'block',
                    }}
                  />
                </div>
                {internalLinks !== null ? (
                  <div data-google-interstitial="false">
                    <InternalLinksSidebar links={internalLinks.sidebar} />
                  </div>
                ) : null}
              </div>
            )}
            {isMobile ? (
              <FullModal
                title="Filtri ricerca"
                onClose={acceptSearch}
                headerCta={
                  <Button
                    onClick={() => {
                      resetFilters();
                    }}
                    design="text"
                    size="small"
                  >
                    Azzera
                  </Button>
                }
                open={filtersDialogOpen}
              >
                <FiltersContainer onApply={acceptSearch} />
              </FullModal>
            ) : null}

            <div
              className={classnames(
                ownClasses.col,
                ownClasses.items,
                'col',
                'items'
              )}
              data-testid="listing-container"
            >
              <FavoritesContainer
                onSave={(urn) => {
                  onSave(urn, 'listing');

                  // Linked to the experiment job-favorite-added
                  if (category.id === CategoryID.OfferteLavoro) {
                    optimizely?.onReady().then(() => {
                      optimizely.track('job-favorite-added');
                    });
                  }
                }}
                onUnsave={(urn) => onUnsave(urn, 'listing')}
                onEngage={onEngage}
              >
                <ItemListContainer
                  seo={seo}
                  macroCategory={
                    getMacroOfCategoryID(search.category.id).friendly
                  }
                  enableOverrideUrgentForPro={enableOverrideUrgentForPro}
                  handleItemClick={handleItemClick}
                />
              </FavoritesContainer>

              <PaginationSelector />

              <div data-google-interstitial="false">
                <LiraLazySlotBelowListing />
              </div>

              {isMobile && items.total > 0 && (
                <div data-google-interstitial="false">
                  <div id={MsiteGAMIds.BOX3} />
                </div>
              )}

              {isMobile ? (
                <FloatingButton
                  search={props.search}
                  filtersConfig={props.filtersConfig}
                  items={items}
                  savedSearchesProvider={savedSearchProvider}
                />
              ) : (
                <SnackBar
                  search={props.search}
                  filtersConfig={props.filtersConfig}
                  items={items}
                  savedSearchesProvider={savedSearchProvider}
                />
              )}
              <PremiumSavedSearchExperiment />
            </div>
          </div>
        </div>
      </div>

      {!!faqData?.faq?.length && <Faq data={faqData} />}

      {internalLinks?.sidebar && isMobile && (
        <MostSearched links={internalLinks?.sidebar} />
      )}

      {internalLinks?.footer && (
        <InternalLinksFooter links={internalLinks.footer} />
      )}

      <Sitemap />
    </div>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state) => ({
    filtersDialogOpen: state.search.filtersDialogOpen,
    category: state.search.category,
    filters: state.search.filters,
    geo: state.search.geo,
    query: state.search.query,
    isRadiusSearch: state.search.radiusSearch != null,
    qso: state.search.qso,
    shp: state.search.includeShippableOnly,
    adType: state.search.adType,
    radiusSearch: state.search.radiusSearch || null,
    searchNearRegions: state.search.searchNearRegions ?? false,
    search: state.search,
    urg: state.search.includeUrgent,
    items: state.items,
    filtersConfig: state.filtersConfig,
  }),
  (dispatch) => ({
    openFiltersDialog: () => {
      batch(() => {
        dispatch(setFiltersDialogStatus(true));
        dispatch(setPreventPopstate(true));
      });
    },
    acceptSearch: () => {
      batch(() => {
        dispatch(setFiltersDialogStatus(false));
        dispatch(setPreventPopstate(false));
        dispatch(declareFinalSearch());
      });
    },
    resetFilters: () => {
      dispatch(resetFilters());
    },
  })
)(ListingContainer);

const NoScriptSeoCss = () => (
  <noscript>
    <style>
      {`
        #banner_container {
          flex: 0 !important;
        }
      `}
    </style>
  </noscript>
);

const Skyscrapers = () => {
  return (
    <>
      <aside
        className={classnames(
          ownClasses['layout__aside'],
          ownClasses['layout__aside--left']
        )}
      >
        <div className={ownClasses['layout__aside__wrapper']}>
          <div
            className={ownClasses['layout__aside__skyscraper']}
            data-google-interstitial="false"
          >
            <LiraAdvContainer
              inlineStyleForAd={{ display: 'block' }}
              idForAd={LiraContainerIds.listing.skyscraperLeft}
            />
          </div>
        </div>
      </aside>
      <aside
        className={classnames(
          ownClasses['layout__aside'],
          ownClasses['layout__aside--right']
        )}
      >
        <div className={ownClasses['layout__aside__wrapper']}>
          <div
            className={ownClasses['layout__aside__skyscraper']}
            data-google-interstitial="false"
          >
            <LiraAdvContainer
              inlineStyleForAd={{ display: 'block' }}
              idForAd={LiraContainerIds.listing.skyscraperRight}
            />
          </div>
        </div>
      </aside>
    </>
  );
};
